import { useState, useEffect, useCallback, useRef } from 'react'
import Breadcrumb from './Breadcrumb'
import getImageUrl from './get-image-url.js'
import PageHeader from './PageHeader.jsx'
import '@mantine/core/styles.css'

import apiFetch from '@wordpress/api-fetch'

import { TextInput, Group, Radio, FileInput, Checkbox } from '@mantine/core'
import { useForm } from '@mantine/form'

import { createTheme, MantineProvider } from '@mantine/core'

import { zodResolver } from 'mantine-form-zod-resolver'
import { z } from 'zod'

import BtnText from './BtnText'

import { useMediaQuery } from 'react-responsive'

import scrollIntoView from 'scroll-into-view-if-needed'

import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'

const scr = (node) => {
  scrollIntoView(node, {
    behavior: 'auto',
    scrollMode: 'if-needed',
    block: 'start',
    inline: 'start',
  })
}

const theme = createTheme({
  /** Put your mantine theme override here */
  /** See full list of overridable components here: https://mantine.dev/theming/overrides */
  fontFamily: 'Noto Sans JP',
})

function Contact() {
  // userStateを宣言
  const [step, setStep] = useState(1)
  const [status, setStatus] = useState(null)
  const [token, setToken] = useState(null)
  const [waiting, setWaiting] = useState(false)
  const response_message = useRef('')

  const { executeRecaptcha } = useGoogleReCaptcha()

  // Create an event handler so you can call the verification on button click event or form submit
  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available')
      return
    }

    const reCaptchaToken = await executeRecaptcha('contact')
    setToken(reCaptchaToken)
    // Do whatever you want with the token
  }, [executeRecaptcha])

  // You can use useEffect to trigger the verification as soon as the component being loaded
  useEffect(() => {
    handleReCaptchaVerify()
  }, [handleReCaptchaVerify])

  const isLG = useMediaQuery({ query: '(min-width: 1024px)' })

  // const schema = z
  //   .object({
  //     permit_area: z.array(z.string()),
  //     permit_tokyo: z.string().optional(),
  //   })
  //   .refine(
  //     (data) => {
  //       // permit_areaに「東京都」が含まれている場合、permit_tokyoは必須
  //       if (data.permit_area.includes('東京都')) {
  //         return data.permit_tokyo !== undefined
  //       }
  //       // それ以外の場合、permit_tokyoは任意
  //       return true
  //     },
  //     { message: 'permit_tokyo is required when permit_area includes 東京都.' }
  //   )

  const schema = z.object({
    certificate_group: z.literal('はい', {
      errorMap: () => ({ message: '' }),
    }),
    certificate: z.instanceof(File, {
      message: 'ファイルを選択してください。',
    }),
    permit_group: z.literal('はい', {
      errorMap: () => ({ message: '' }),
    }),
    permit_tokyo: z.any().refine(
      (value) => {
        if (form.values.permit_area.includes('東京都')) {
          return value instanceof File
        }
        // それ以外の場合、permit_tokyoは任意
        return true
      },
      { message: 'ファイルを選択してください。' }
    ),
    permit_kanagawa: z.any().refine(
      (value) => {
        if (form.values.permit_area.includes('神奈川県')) {
          return value instanceof File
        }
        return true
      },
      { message: 'ファイルを選択してください。' }
    ),
    permit_saitama: z.any().refine(
      (value) => {
        if (form.values.permit_area.includes('埼玉県')) {
          return value instanceof File
        }
        return true
      },
      { message: 'ファイルを選択してください。' }
    ),
    permit_chiba: z.any().refine(
      (value) => {
        if (form.values.permit_area.includes('千葉県')) {
          return value instanceof File
        }
        return true
      },
      { message: 'ファイルを選択してください。' }
    ),
    permit_shizuoka: z.any().refine(
      (value) => {
        if (form.values.permit_area.includes('静岡県')) {
          return value instanceof File
        }
        return true
      },
      { message: 'ファイルを選択してください。' }
    ),
    permit_other: z.any().refine(
      (value) => {
        if (form.values.permit_area.includes('その他')) {
          return value instanceof File
        }
        return true
      },
      { message: 'ファイルを選択してください。' }
    ),
    permit_area: z
      .array(z.string(), {
        errorMap: () => ({ message: '地域を選択してください。' }),
      })
      .min(1, { message: '地域を選択してください。' }),
    permit_area_other: z.string().refine(
      (value) => {
        if (form.values.permit_area.includes('その他')) {
          return value.length > 0
        }
        return true
      },
      { message: '入力してください' }
    ),
    insurance_group: z.literal('はい', {
      errorMap: () => ({ message: '' }),
    }),
    insurance: z.instanceof(File, { message: 'ファイルを選択してください。' }),
    company_name: z.string(),
    company_name_kana: z
      .string()
      .optional()
      .refine((value) => /^[ァ-ヴー　 ]*$/.test(value), {
        message: '全角カタカナで入力してください。',
      }),
    representative_name: z.string().min(1, { message: 'この項目は必須です' }),
    representative_name_kana: z
      .string()
      .regex(/^[ァ-ヴー　 ]+$/, { message: '全角カタカナで入力してください。' })
      .min(1, { message: 'この項目は必須です' }),
    person_in_charge_name: z.string().min(1, { message: 'この項目は必須です' }),
    person_in_charge_name_kana: z
      .string()
      .regex(/^[ァ-ヴー　 ]+$/, { message: '全角カタカナで入力してください。' })
      .min(1, { message: 'この項目は必須です' }),
    office_address_zip: z
      .string()
      .regex(/^[0-9]{3}-?[0-9]{4}$/, {
        message: '有効な郵便番号を入力してください。',
      })
      .min(1, { message: 'この項目は必須です' }),
    office_address: z.string().min(1, { message: 'この項目は必須です' }),
    phone_number: z
      .string()
      .regex(/^[0-9]{2,3}-?[0-9]{4}-?[0-9]{4}$/, {
        message: '有効な電話番号を入力してください。',
      })
      .min(1, { message: 'この項目は必須です' }),
    fax: z.union([
      z.string().regex(/^[0-9]{2,3}-?[0-9]{4}-?[0-9]{4}$/, {
        message: '有効なFAX番号を入力してください。',
      }),
      z.string().optional(),
    ]),
    email: z
      .string()
      .email({ message: '有効なメールアドレスを入力してください。' })
      .min(1, { message: 'この項目は必須です' }),
  })

  const form = useForm({
    initialValues: {
      certificate_group: 'はい',
      certificate: null,
      permit_group: 'はい',
      permit_tokyo: null,
      permit_kanagawa: null,
      permit_saitama: null,
      permit_chiba: null,
      permit_shizuoka: null,
      permit_other: null,
      permit_area: [],
      permit_area_other: '',
      insurance_group: 'はい',
      insurance: null,
      company_name: '',
      company_name_kana: '',
      representative_name: '',
      representative_name_kana: '',
      person_in_charge_name: '',
      person_in_charge_name_kana: '',
      office_address_zip: '',
      office_address: '',
      phone_number: '',
      fax: '',
      email: '',
    },
    validate: zodResolver(schema),
    validateInputOnBlur: true,
  })

  const confirm = () => {
    form.validate()

    if (form.isValid()) {
      setStep(2)
    }

    scr(document.querySelector('.form-wrapper'))
  }

  const permit_arr = [
    {
      label: '東京都',
      value: 'permit_tokyo',
    },
    {
      label: '神奈川県',
      value: 'permit_kanagawa',
    },
    {
      label: '埼玉県',
      value: 'permit_saitama',
    },
    {
      label: '千葉県',
      value: 'permit_chiba',
    },
    {
      label: '静岡県',
      value: 'permit_shizuoka',
    },
    {
      label: 'その他',
      value: 'permit_other',
    },
  ]

  const post = (raw) => {
    setWaiting(true)

    const obj = {
      certificate_group: raw.certificate_group,
      permit_group: raw.permit_group,
      permit_area: raw.permit_area,
      permit_area_other: raw.permit_area_other,
      insurance_group: raw.insurance_group,
      company_name: raw.company_name,
      company_name_kana: raw.company_name_kana,
      representative_name: raw.representative_name,
      representative_name_kana: raw.representative_name_kana,
      person_in_charge_name: raw.person_in_charge_name,
      person_in_charge_name_kana: raw.person_in_charge_name_kana,
      office_address_zip: raw.office_address_zip,
      office_address: raw.office_address,
      phone_number: raw.phone_number,
      fax: raw.fax,
      email: raw.email,
    }

    const data = new FormData()

    for (const key in obj) {
      data.append(key, obj[key])
    }

    if (raw.certificate) {
      data.append('certificate', raw.certificate)
    }

    if (raw.permit_tokyo) {
      data.append('permit_tokyo', raw.permit_tokyo)
    }

    if (raw.permit_kanagawa) {
      data.append('permit_kanagawa', raw.permit_kanagawa)
    }

    if (raw.permit_saitama) {
      data.append('permit_saitama', raw.permit_saitama)
    }

    if (raw.permit_chiba) {
      data.append('permit_chiba', raw.permit_chiba)
    }

    if (raw.permit_shizuoka) {
      data.append('permit_shizuoka', raw.permit_shizuoka)
    }

    if (raw.permit_other) {
      data.append('permit_other', raw.permit_other)
    }

    if (raw.insurance) {
      data.append('insurance', raw.insurance)
    }

    data.append('recaptcha_token', token)

    apiFetch({
      path: `${import.meta.env.VITE_SITE_URL}/wp-json/myroutes/recruitment_of_partner_companies/`,
      method: 'POST',
      body: data,
    })
      .then((res) => {
        if (res.status === 'success') {
          setStatus('success')
          setStep(3)
          scr(document.querySelector('.content-area'))
        } else {
          setStatus('error')
          response_message.current = res.message
          console.table(res)
          setStep(4)
          scr(document.querySelector('.content-area'))
        }
        setWaiting(false)
      })
      .catch((error) => {
        setStatus('error')
        response_message.current = error.message
        console.table(error)
        setStep(4)
        setWaiting(false)
        scr(document.querySelector('.content-area'))
      })
  }

  return (
    <>
      <PageHeader
        title={'協力会社様募集'}
        image={getImageUrl('RECRUITMENT-OFPARTNER-COMPANIES.png')}
      />
      <div className="content-area">
        <Breadcrumb
          current={'協力会社様募集'}
          site_url={import.meta.env.VITE_SITE_URL}
          items={[]}
        />
      </div>
      <div>
        <div className="py-[calc(84vw/7.5)] md:pt-[110px] md:pb-[60px]">
          <h1 className={status === 'success' ? ' lg:!text-[56px]' : null}>
            {status === 'success' ? 'ご応募ありがとうございます' : '協力会社様募集フォーム'}
          </h1>
        </div>
        {(step === 1 || step === 2) && (
          <div className="form-wrapper">
            <div className="form-inner content-area">
              <MantineProvider theme={theme}>
                <form onSubmit={form.onSubmit((values) => post(values))}>
                  {step === 1 && (
                    <>
                      <h2>解体工事業登録、もしくは建設業許可（解体工事業）をお持ちですか？</h2>
                      <Radio.Group {...form.getInputProps('certificate_group')}>
                        <Group mt={20}>
                          <Radio
                            label="はい"
                            value="はい"
                          />
                          <Radio
                            label="いいえ"
                            value="いいえ"
                          />
                        </Group>
                      </Radio.Group>
                      {form.values.certificate_group === 'はい' ? (
                        <div className="flex gap-[48px] mt-[40px]">
                          <div className="w-[50px]">はい</div>
                          <div>
                            <p>各自治体で発行された登録証を添付してください</p>
                            <div className="flex flex-wrap gap-x-[14px] items-center mt-[12px]">
                              <FileInput
                                variant="unstyled"
                                placeholder="登録証を添付する"
                                valueComponent={() => {
                                  return '登録証を添付する'
                                }}
                                {...form.getInputProps('certificate')}
                                styles={{
                                  input: {
                                    border: '2px solid #b4b4b4',
                                    borderRadius: '999px',
                                    padding: '0px 20px',
                                  },
                                }}
                              />
                              <p className="filename">{form.values.certificate?.name}</p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex gap-[48px] mt-[40px]">
                          <div className="w-[50px]">いいえ</div>
                          <div className="text-[#FF2626]">※募集対象ではありません</div>
                        </div>
                      )}

                      <h2 className="mt-[90px]">産業廃棄物収集運搬許可証をお持ちですか？</h2>
                      <Radio.Group {...form.getInputProps('permit_group')}>
                        <Group mt={20}>
                          <Radio
                            label="はい"
                            value="はい"
                          />
                          <Radio
                            label="いいえ"
                            value="いいえ"
                          />
                        </Group>
                      </Radio.Group>
                      {form.values.permit_group === 'はい' ? (
                        <>
                          <h3 className=" mt-[40px]">許可書取得地域</h3>
                          <div className="flex items-center gap-2">
                            <Checkbox.Group {...form.getInputProps('permit_area')}>
                              <Group mt="xs">
                                <Checkbox
                                  checked
                                  label="東京都"
                                  value="東京都"
                                />
                                <Checkbox
                                  label="神奈川県"
                                  value="神奈川県"
                                />
                                <Checkbox
                                  label="埼玉県"
                                  value="埼玉県"
                                />
                                <Checkbox
                                  label="千葉県"
                                  value="千葉県"
                                />
                                <Checkbox
                                  label="静岡県"
                                  value="静岡県"
                                />
                                <Checkbox
                                  label="その他"
                                  value="その他"
                                />
                              </Group>
                            </Checkbox.Group>
                            {form.values.permit_area.includes('その他') && (
                              <div className="pt-3">
                                <TextInput
                                  {...form.getInputProps('permit_area_other')}
                                  styles={{ root: { width: '100px' } }}
                                />
                              </div>
                            )}
                          </div>
                          {permit_arr.map((item, index) => {
                            if (form.values.permit_area.includes(item.label)) {
                              return (
                                <div
                                  className="flex gap-[48px] mt-[40px]"
                                  key={index}
                                >
                                  <div className="w-[70px]">{item.label}</div>
                                  <div>
                                    <p>各自治体で発行された許可証を添付してください</p>
                                    <div className="flex flex-wrap gap-x-[14px] items-center mt-[12px]">
                                      <FileInput
                                        variant="unstyled"
                                        placeholder="許可証を添付する"
                                        valueComponent={() => {
                                          return '許可証を添付する'
                                        }}
                                        {...form.getInputProps(item.value)}
                                        styles={{
                                          input: {
                                            border: '2px solid #b4b4b4',
                                            borderRadius: '999px',
                                            padding: '0px 20px',
                                          },
                                        }}
                                      />
                                    </div>

                                    <div className="mt-2">
                                      <p className="filename">{form.values[item.value]?.name}</p>
                                    </div>
                                  </div>
                                </div>
                              )
                            }
                          })}
                        </>
                      ) : (
                        <div className="flex gap-[48px] mt-[40px]">
                          <div className="w-[50px]">いいえ</div>
                          <div className="text-[#FF2626]">※募集対象ではありません</div>
                        </div>
                      )}

                      <h2 className="mt-[90px]">
                        解体工事業者対象の損害賠償保険（または同等の保険）に加入されていますか？
                      </h2>
                      <Radio.Group {...form.getInputProps('insurance_group')}>
                        <Group mt={20}>
                          <Radio
                            label="はい"
                            value="はい"
                          />
                          <Radio
                            label="いいえ"
                            value="いいえ"
                          />
                        </Group>
                      </Radio.Group>
                      {form.values.insurance_group === 'はい' ? (
                        <div className="flex gap-[48px] mt-[40px]">
                          <div>はい</div>
                          <div>
                            <p>
                              加入されていること・補償内容が分かる書面（保険証券など）を添付してください
                            </p>
                            <div className="flex flex-wrap gap-x-[14px] items-center mt-[12px]">
                              <FileInput
                                variant="unstyled"
                                placeholder="書面を添付する"
                                valueComponent={() => {
                                  return '書面を添付する'
                                }}
                                {...form.getInputProps('insurance')}
                                styles={{
                                  input: {
                                    border: '2px solid #b4b4b4',
                                    borderRadius: '999px',
                                    padding: '0px 20px',
                                  },
                                }}
                              />
                              <p className="filename">{form.values.insurance?.name}</p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex gap-[48px] mt-[40px]">
                          <div>いいえ</div>
                          <div className="text-[#FF2626]">※募集対象ではありません</div>
                        </div>
                      )}
                      <p className="pt-[106px] paragrah-2">
                        <span className="text-[#CC4A1A]">*</span>
                        は入力必須項目です。
                      </p>
                      <dl className="mt-[40px]">
                        <dt>会社名</dt>
                        <dd>
                          <TextInput {...form.getInputProps('company_name')} />
                        </dd>
                        <dt>会社名（フリガナ）</dt>
                        <dd>
                          <TextInput {...form.getInputProps('company_name_kana')} />
                        </dd>
                        <dt>
                          代表者名<span>*</span>
                        </dt>
                        <dd>
                          <TextInput {...form.getInputProps('representative_name')} />
                        </dd>
                        <dt>
                          代表者名（フリガナ）<span>*</span>
                        </dt>
                        <dd>
                          <TextInput {...form.getInputProps('representative_name_kana')} />
                        </dd>
                        <dt>
                          担当者名<span>*</span>
                        </dt>
                        <dd>
                          <TextInput {...form.getInputProps('person_in_charge_name')} />
                        </dd>
                        <dt>
                          担当者名（フリガナ）<span>*</span>
                        </dt>
                        <dd>
                          <TextInput {...form.getInputProps('person_in_charge_name_kana')} />
                        </dd>
                        <dt>
                          事務所住所（〒）<span>*</span>
                        </dt>
                        <dd>
                          <TextInput {...form.getInputProps('office_address_zip')} />
                        </dd>
                        <dt>
                          事務所住所<span>*</span>
                        </dt>
                        <dd>
                          <TextInput {...form.getInputProps('office_address')} />
                        </dd>
                        <dt>
                          電話番号<span>*</span>
                        </dt>
                        <dd>
                          <TextInput {...form.getInputProps('phone_number')} />
                        </dd>
                        <dt>FAX番号</dt>
                        <dd>
                          <TextInput {...form.getInputProps('fax')} />
                        </dd>
                        <dt>
                          メールアドレス<span>*</span>
                        </dt>
                        <dd>
                          <TextInput {...form.getInputProps('email')} />
                        </dd>
                      </dl>
                      <BtnText
                        className="m-[68px_auto_0]"
                        onClick={() => confirm()}
                        arrow={false}
                      >
                        確認する
                      </BtnText>
                    </>
                  )}
                  {step === 2 && (
                    <>
                      <h2>解体工事業登録、もしくは建設業許可（解体工事業）をお持ちですか？</h2>
                      <div className="flex gap-[48px] mt-[40px]">
                        <div className="w-[50px]">はい</div>
                        <div>
                          <p>各自治体で発行された登録証を添付してください</p>
                          <p className="paragrah-1">{form.values.certificate?.name}</p>
                        </div>
                      </div>

                      <h2 className="mt-[90px]">産業廃棄物収集運搬許可証をお持ちですか？</h2>

                      <div className="flex gap-[48px] mt-[40px]">
                        <div className="w-[140px]">許可書取得地域</div>
                        <div>
                          <p>
                            {form.values.permit_area.map((item, index) => {
                              if (index === 0) {
                                return item
                              }
                              return `, ${item}`
                            })}
                            {form.values.permit_area.includes('その他') &&
                              ` ${form.values.permit_area_other}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-[48px] mt-[40px]">
                        <div className="w-[50px]">はい</div>
                        <div>
                          <p>各自治体で発行された登録証を添付してください</p>
                          {permit_arr.map((item, index) => {
                            if (form.values.permit_area.includes(item.label)) {
                              return (
                                <p
                                  className="paragrah-1"
                                  key={index}
                                >
                                  {form.values[item.value]?.name}
                                </p>
                              )
                            }
                          })}
                        </div>
                      </div>

                      <h2 className="mt-[90px]">
                        解体工事業者対象の損害賠償保険（または同等の保険）に加入されていますか？
                      </h2>
                      <div className="flex gap-[48px] mt-[40px]">
                        <div>はい</div>
                        <div>
                          <p>
                            加入されていること・補償内容が分かる書面（保険証券など）を添付してください
                          </p>
                          <p className="paragrah-1">{form.values.insurance?.name}</p>
                        </div>
                      </div>
                      <p className="pt-[106px] paragrah-2">
                        <span className="text-[#CC4A1A]">*</span>
                        は入力必須項目です。
                      </p>
                      <dl className="mt-[40px]">
                        <dt>会社名</dt>
                        <dd>{form.values.company_name}</dd>
                        <dt>会社名（フリガナ）</dt>
                        <dd>{form.values.company_name_kana}</dd>
                        <dt>
                          代表者名<span>*</span>
                        </dt>
                        <dd>{form.values.representative_name}</dd>
                        <dt>
                          代表者名（フリガナ）<span>*</span>
                        </dt>
                        <dd>{form.values.representative_name_kana}</dd>
                        <dt>
                          担当者名<span>*</span>
                        </dt>
                        <dd>{form.values.person_in_charge_name}</dd>
                        <dt>
                          担当者名（フリガナ）<span>*</span>
                        </dt>
                        <dd>{form.values.person_in_charge_name_kana}</dd>
                        <dt>
                          事務所住所（〒）<span>*</span>
                        </dt>
                        <dd>{form.values.office_address_zip}</dd>
                        <dt>
                          事務所住所<span>*</span>
                        </dt>
                        <dd>{form.values.office_address}</dd>
                        <dt>
                          電話番号<span>*</span>
                        </dt>
                        <dd>{form.values.phone_number}</dd>
                        <dt>FAX番号</dt>
                        <dd>{form.values.fax}</dd>
                        <dt>
                          メールアドレス<span>*</span>
                        </dt>
                        <dd>{form.values.email}</dd>
                      </dl>
                      <div className="flex flex-col md:flex-row justify-center items-center gap-4 mt-12">
                        <BtnText
                          onClick={() => {
                            setStep(1)
                            scr(document.querySelector('.content-area'))
                          }}
                          white={true}
                          className="!border-[#003475] !border max-w-xs"
                          arrow={false}
                          disabled={waiting}
                        >
                          戻る
                        </BtnText>
                        <BtnText
                          arrow={false}
                          className="max-w-xs"
                          type="submit"
                          onClick={() => scr(document.querySelector('.content-area'))}
                          disabled={waiting}
                        >
                          送信する
                        </BtnText>
                      </div>
                    </>
                  )}
                </form>
              </MantineProvider>
            </div>
          </div>
        )}
        {step === 3 && (
          <>
            <p className="paragrah-3">
              確認のために、自動返信メールをお送りしております。
              <br />
              万が一、自動返信メールが届かない場合は、
              {isLG ? <br /> : ''}
              お手数ですがお電話にてお問い合わせください。
            </p>
            <div className="flex justify-center mt-[60px] lg:mt-[100px]">
              <BtnText
                href={import.meta.env.VITE_SITE_URL}
                arrow={false}
              >
                トップページへ
              </BtnText>
            </div>
          </>
        )}
        {step === 4 && (
          <>
            <h3
              style={{
                textAlign: 'center',
                font: 'normal normal bold 30px/44px Noto Sans JP',
                color: 'red',
                marginBottom: '20px',
              }}
            >
              送信エラー
            </h3>
            <p
              style={{
                textAlign: 'center',
                fontSize: '16px',
                marginBottom: '20px',
                fontWeight: 'bold',
              }}
            >
              {response_message.current}
            </p>
            <p className="paragrah-3">
              申し訳ございません、送信に失敗しました。
              <br />
              セキュリティーソフトやアプリをオフにして頂き再度お試しいただくか、
              {isLG ? <br /> : ''}
              お手数ですがお電話にてお問い合わせください。
            </p>
            <div className="flex justify-center mt-[60px] lg:mt-[100px]">
              <BtnText
                href={import.meta.env.VITE_SITE_URL}
                arrow={false}
              >
                トップページへ
              </BtnText>
            </div>
          </>
        )}
      </div>
      <style jsx>{`
        h1 {
          text-align: center;
          font: normal normal bold calc(50vw / 7.5) Noto Sans JP;
          color: #2964b6;
        }
        .paragrah-1 {
          text-align: center;
          font: normal normal 500 calc(30vw / 7.5) Noto Sans JP;
        }
        .paragrah-3 {
          font: normal normal normal calc(35vw / 7.5) Noto Sans JP;
          padding: 0 calc(70vw / 7.5);
        }
        .form-inner {
          background: #ffffff 0% 0% no-repeat padding-box;
          padding-bottom: calc(214vw / 7.5);
        }
        .btn-1 {
          background: #003475 0% 0% no-repeat padding-box;
          border-radius: 6px;
          font: normal normal bold 20px/27px Noto Sans JP;
          color: #ffffff;
          padding: 20px 120px;
          margin: 68px auto 0;
          display: block;
          border: 2px solid transparent;
        }
        .form-link {
          text-decoration: underline;
          font: normal normal 500 20px/29px Noto Sans JP;
        }
        .form-policy {
          background: #f7f7f7 0% 0% no-repeat padding-box;
          padding: calc(86vw / 7.5) calc(70vw / 7.5);
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .form-policy .inner {
          max-width: 704px;
        }
        .form-policy h3 {
          font: normal normal bold 30px/44px Noto Sans JP;
          text-align: center;
        }
        .form-policy h4 {
          font: normal normal bold 20px/29px Noto Sans JP;
        }
        .form-policy p {
          font: normal normal normal 16px/24px Noto Sans JP;
        }
        dt:last-of-type,
        dd:last-of-type {
          border-bottom: none;
        }
        dt {
          width: 240px;
          font: normal normal normal 20px Noto Sans JP;
        }
        dt span {
          color: #cc4a1a;
        }
        dd {
          padding: calc(15vw / 7.5) 0 calc(22vw / 7.5);
        }

        @media screen and (min-width: 768px) {
          h1 {
            text-align: center;
            font: normal normal bold 80px/80px Noto Sans JP;
            color: #2964b6;
          }
          h2 {
            background: #2964b6 0% 0% no-repeat padding-box;
            font: normal normal bold 19px/36px Noto Sans JP;
            color: #ffffff;
            padding: 0 12px;
          }
          .paragrah-1 {
            font: normal normal normal 18px/43px Noto Sans JP;
            color: #b4b4b4;
          }
          .filename {
            font: normal normal normal 16px/21px Noto Sans JP;
            color: #b4b4b4;
          }
          .paragrah-2 {
            text-align: center;
            font: normal normal 500 20px/58px Noto Sans JP;
          }
          .paragrah-3 {
            text-align: center;
            font: normal normal normal 25px/50px Noto Sans JP;

            padding: 0;
          }
          .form-wrapper {
            background-image: linear-gradient(180deg, #2964b6 0%, #2760b3 100%),
              url(${getImageUrl('form-bg.png')});
            background-size: cover, 1920 699px;
            background-repeat: no-repeat;
            background-position: bottom;
            background-blend-mode: multiply;
            padding: 106px 0;
          }
          .form-inner {
            background: #ffffff 0% 0% no-repeat padding-box;
            border-radius: 40px;
            padding: 80px 150px;
          }
          .btn-1 {
            background: #003475 0% 0% no-repeat padding-box;
            border-radius: 6px;
            font: normal normal bold 20px/27px Noto Sans JP;
            color: #ffffff;
            padding: 20px 120px;
            margin: 68px auto 0;
            display: block;
            border: 2px solid transparent;
          }
          .btn-2 {
            background: #fff 0% 0% no-repeat padding-box;
            border-radius: 6px;
            font: normal normal bold 20px/27px Noto Sans JP;
            color: #003475;
            padding: 20px 120px;
            margin: 68px auto 0;
            display: block;
            border: 2px solid #003475;
          }
          dl {
            display: grid;
            grid-template-columns: auto 1fr;
          }
          dt,
          dd {
            border-bottom: 1px solid #b4b4b4;
            padding: 10px 0;
          }
          dt:last-of-type,
          dd:last-of-type {
            border-bottom: none;
          }
          dt {
            width: 240px;
          }
          dt span {
            color: #cc4a1a;
          }
        }
      `}</style>
    </>
  )
}

export default Contact
